import * as dayjs from 'dayjs';

// "Use project dates": a task that simply runs from its project's start date to its end date.
//
// The task's relative offsets are stored to match as well - start 0 days after the project start, due
// USE_PROJECT_DATES_DUE_OFFSET_DAYS after it - so anything that only reads the offsets (e.g. the API's
// task creation, which then clamps a due date past the project end back to the project end) still
// lands on exactly the project end, whatever the length of each repeated project.
export const USE_PROJECT_DATES_DUE_OFFSET_DAYS = 36500;

// Sets the offsets that stand for "use project dates" (see above).
export function SetUseProjectDatesOffsets(task: any) {
  task.relative_date = true;
  task.relative_to_start = true;
  task.start_date_relative = 0;
  task.start_date_relative_unit = 'days';
  task.due_date_relative = USE_PROJECT_DATES_DUE_OFFSET_DAYS;
  task.due_date_relative_unit = 'days';
  task.start_on_project_start = false;
  task.due_on_project_end = false;
}

// A relative task's start date can be pinned to the project start, and its due date to the project end,
// whichever project date the offsets are counted from. An offset from the other end can't do that once
// project lengths vary - e.g. due 28 days before the project end and "starting 30 days before the end" lands
// 1 day late in a 31-day month - so these always land exactly on the project's own date.
export function StartsOnProjectStart(task: any): boolean {
  return !!task?.relative_date && !!task.start_on_project_start;
}

export function DueOnProjectEnd(task: any): boolean {
  return !!task?.relative_date && !!task.due_on_project_end;
}

// How a relative task's start / due date reads in task lists, including "use project dates" and the pins above.
export function TaskStartDateLabel(task: any): string {
  if (UsesProjectDates(task) || StartsOnProjectStart(task)) return 'On project start';
  return RelativeTaskDateLabel(task?.start_date_relative, task?.start_date_relative_unit, task?.relative_to_start);
}

export function TaskDueDateLabel(task: any): string {
  if (UsesProjectDates(task) || DueOnProjectEnd(task)) return 'On project end';
  return RelativeTaskDateLabel(task?.due_date_relative, task?.due_date_relative_unit, task?.relative_to_start);
}

// Whether a task uses its project's dates - by its flag, or by the offsets that stand for it (in case the
// flag itself wasn't kept, e.g. a task that went through the API, which only stores the offsets).
export function UsesProjectDates(task: any): boolean {
  if (!task) return false;
  if (task.use_project_dates) return true;
  return !!task.relative_date && !!task.relative_to_start && Number(task.start_date_relative) === 0 &&
    Number(task.due_date_relative) === USE_PROJECT_DATES_DUE_OFFSET_DAYS;
}

// How a task date set relative to the project reads, e.g. "3 days after project start". An offset of 0
// lands exactly on the project date, so it just reads "On project start" / "On project end".
export function RelativeTaskDateLabel(offset: any, unit: any, relative_to_start: boolean): string {
  if (!Number(offset)) return relative_to_start ? 'On project start' : 'On project end';
  return `${offset} ${unit || 'days'} ${relative_to_start ? 'after project start' : 'before project end'}`;
}

// Works out a relative task's actual start/due dates from its offsets and the project's dates
// ("use project dates" tasks land exactly on the project's start/end).
export function CalculateRelativeTaskDates(task: any, project_start_date: any, project_end_date: any) {
  if (task.relative_to_start) {
    task.start_date = dayjs(project_start_date).add(task.start_date_relative, task.start_date_relative_unit).format('YYYY-MM-DD');
    task.due_date = dayjs(project_start_date).add(task.due_date_relative, task.due_date_relative_unit).format('YYYY-MM-DD');
  }
  else {
    task.start_date = dayjs(project_end_date).add(task.start_date_relative * -1, task.start_date_relative_unit).format('YYYY-MM-DD');
    task.due_date = dayjs(project_end_date).add(task.due_date_relative * -1, task.due_date_relative_unit).format('YYYY-MM-DD');
  }
  ApplyUseProjectDates(task, project_start_date, project_end_date);
}

// Why a task's dates don't fit its project, or '' when they do.
export function TaskDateError(task: any, project_start_date: any, project_end_date: any): string {
  if (!task.start_date || !task.due_date) return 'Task start and due dates are required.';
  if (task.due_date > dayjs(project_end_date).format('YYYY-MM-DD')) return 'Task due date must be on or before the project end date.';
  if (task.start_date < dayjs(project_start_date).format('YYYY-MM-DD')) return 'Task start date must be on or after the project start date.';
  if (task.start_date > task.due_date) return 'Task start date must be before or equal to the due date.';
  return '';
}

// Actions are always due on their task's due date - there's no per-action due date option. Keeps
// relative_date / due_date_relative populated so the JSON still carries the fields the backend expects.
export function AlignStepDueDateToTask(task: any, step: any) {
  step.relative_date = true;
  step.due_date_relative = (task.due_date && task.start_date) ? Math.max(0, dayjs(task.due_date).diff(dayjs(task.start_date), 'day')) : 0;
  step.due_date = dayjs(task.start_date).add(step.due_date_relative, 'day').format('YYYY-MM-DD');
  if (step.auto_start_days_before != null) {
    step.auto_start_date = dayjs(step.due_date).add(-step.auto_start_days_before, 'day').format('YYYY-MM-DD');
  }
}

// For a "use project dates" task, sets its dates to the project's start/end and returns true -
// call it after the usual offset calculation so it wins. Otherwise returns false, after applying a start
// pinned to the project start / due pinned to the project end, if the task has either (see StartsOnProjectStart).
export function ApplyUseProjectDates(task: any, project_start_date: any, project_end_date: any): boolean {
  if (!UsesProjectDates(task)) {
    if (StartsOnProjectStart(task)) task.start_date = dayjs(project_start_date).format('YYYY-MM-DD');
    if (DueOnProjectEnd(task)) task.due_date = dayjs(project_end_date).format('YYYY-MM-DD');
    return false;
  }
  task.use_project_dates = true;
  task.start_date = dayjs(project_start_date).format('YYYY-MM-DD');
  task.due_date = dayjs(project_end_date).format('YYYY-MM-DD');
  return true;
}
